import { SyncIcon } from '@mm/icons';
import { ReactComponent as FacebookIcon } from 'assets/facebook.svg';
import classNames from 'classnames';
import React from 'react';
import { useSelector } from 'react-redux';
import ConnectedPageModel from '@mm/state/model/ConnectedPageModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { useConnectedPageCRUDActions } from '@mm/state/actions/ConnectedPageCRUDActions';
import {
  activeFacebookPagePlanInfoSelector,
  activeFacebookPageSelector,
  selectBasicPages,
} from '@mm/state/selectors/facebookPageSelector';
import { Status } from '@mm/components';
import { colors } from '../../../../../beta/styles/colors';
import SettingSection from '../../../../components/SettingSection/SettingSection';
import './FacebookConnectionStatus.scss';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { TooltipGuard } from '../../../../../../../modules/permissions/RequiresPermissions';

type ConnectedPage = ConnectedPageModel | undefined;

type Props = {
  connectedPage: ConnectedPage;
  refreshAccessToken: (pageId: number) => Promise<void>;
  companyId: number;
};

export const getConnectionStatus = (page: ConnectedPage) => {
  if (page && page.tokenStatus) {
    const { tokenStatus } = page;

    if (tokenStatus.status === 'ok') return ['OK', colors.green];
    if (tokenStatus.status === 'failed') return ['Error', colors.danger];
    return [tokenStatus.status, colors.danger];
  }
  return ['Unknown', colors.danger];
};

const FacebookConnectionStatus = ({
  connectedPage,
  refreshAccessToken,
  companyId,
}: Props) => {
  const [connectionStatus, connectionColor] =
    getConnectionStatus(connectedPage);
  const activePage = useSelector(activeFacebookPageSelector);
  const facebookPagesInActiveCompany = useSelector(selectBasicPages);
  const {
    openDisconnectPageModal,
    openDowngradeFacebookPageModal,
    openGeneralConfirmModal,
  } = useModalActions();
  const { retrieveBasicListByCompany } = useConnectedPageCRUDActions();
  const statusText = connectionStatus === 'OK' ? 'Connected' : connectionStatus;
  const { info } = useSelector(activeFacebookPagePlanInfoSelector);

  let isLastPageOnTeamSubscription =
    facebookPagesInActiveCompany.length === 1 &&
    facebookPagesInActiveCompany[0].id === connectedPage?.id;

  const onOpenDisconnectModal = async () => {
    const retrivedFbPages = await retrieveBasicListByCompany(companyId);
    if (retrivedFbPages) {
      isLastPageOnTeamSubscription =
        retrivedFbPages.length === 1 &&
        retrivedFbPages[0].id === connectedPage?.id;
    }

    if (activePage) {
      if (
        info.isFreePlan ||
        (info.isAnyTeamPlan && !isLastPageOnTeamSubscription) ||
        activePage?.redemption?.freeTrial
      ) {
        if (connectionStatus !== 'OK') {
          openGeneralConfirmModal({
            headerText: 'Cannot disconnect with an invalid token',
            messageText: (
              <div>
                Your page has an invalid token. Before disconnecting it from
                Customers.ai you need to refresh your token by clicking the
                <b> "Refresh Token"</b> button below.
              </div>
            ),
            okButtonText: 'Refresh Token',
            okButtonStyling: { blue: true },
            cancelButtonFirst: true,
            onCloseHandler: (ok: boolean) =>
              ok && refreshAccessToken(activePage.id),
          });
        } else {
          openDisconnectPageModal(activePage);
        }
      } else {
        openDowngradeFacebookPageModal({
          page: activePage,
          downgradeType: 'disconnecting',
        });
      }
    }
  };

  const statusClassname = classNames(
    'facebook-connection-status__status-text',
    {
      'facebook-connection-status__status-text--error':
        connectionStatus !== 'OK',
    },
  );
  return (
    <SettingSection
      childrenClassName="facebook-connection-status__content"
      title=""
    >
      <div className="facebook-connection-status__title-wrapper">
        <FacebookIcon className="facebook-connection-status__facebook-icon" />
        <div className="facebook-connection-status__status">
          Facebook Connection Status:
        </div>
        <Status
          status={statusText}
          color={connectionColor}
          className={statusClassname}
        />
      </div>

      {connectedPage && (
        <div className="page-settings__item">
          <TooltipGuard position="top">
            {isAllowed => (
              <PrimaryButton
                className="page-settings__refresh-button"
                leftIcon={<SyncIcon />}
                color={ButtonColor.secondaryBlue}
                disabled={!isAllowed}
                onClick={() => refreshAccessToken(connectedPage.id)}
              >
                Refresh Connection
              </PrimaryButton>
            )}
          </TooltipGuard>
          <PrimaryButton
            className="page-settings__disconnect-button"
            color={ButtonColor.invertedRed}
            onClick={onOpenDisconnectModal}
          >
            Disconnect
          </PrimaryButton>
        </div>
      )}
    </SettingSection>
  );
};

export default FacebookConnectionStatus;
